<script setup lang="ts" generic="T extends Record<string, any>">
import type { BulletLegendItemInterface } from "@unovis/ts";
import { VisAxis, VisLine, VisXYContainer } from "@unovis/vue";
import { Axis, CurveType } from "@unovis/ts";
import { type Component, computed, ref } from "vue";
import { useMounted } from "@vueuse/core";
import type { BaseChartProps } from "../chart-bar";
import {
    ChartCrosshair,
    ChartLegend,
    defaultColors,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";

const props = withDefaults(
    defineProps<
        BaseChartProps<T> & {
            /**
             * Render custom tooltip component.
             */
            customTooltip?: Component;
            /**
             * Line curve type
             * @default "linear"
             */
            curveType?: "linear" | "smooth";
            /**
             * Line width
             * @default 2
             */
            lineWidth?: number;
        }
    >(),
    {
        curveType: "smooth",
        lineWidth: 2,
        margin: () => ({ top: 0, bottom: 0, left: 0, right: 0 }),
        filterOpacity: 0.2,
        showXAxis: true,
        showYAxis: true,
        showTooltip: true,
        showLegend: true,
        showGridLine: true,
    },
);
const emits = defineEmits<{
    legendItemClick: [d: BulletLegendItemInterface, i: number];
}>();

type KeyOfT = Extract<keyof T, string>;
type Data = (typeof props.data)[number];

const index = computed(() => props.index as KeyOfT);
const colors = computed(() =>
    props.colors?.length
        ? props.colors
        : defaultColors(props.categories.length),
);
const legendItems = ref<BulletLegendItemInterface[]>(
    props.categories.map((category, i) => ({
        name: category,
        color: colors.value[i],
        inactive: false,
    })),
);

const isMounted = useMounted();

function handleLegendItemClick(d: BulletLegendItemInterface, i: number) {
    emits("legendItemClick", d, i);
}

const curveTypeValue = computed(() =>
    props.curveType === "smooth" ? CurveType.MonotoneX : CurveType.Linear,
);
</script>

<template>
    <div
        :class="
            cn('flex h-[400px] w-full flex-col items-end', $attrs.class ?? '')
        "
    >
        <ChartLegend
            v-if="showLegend"
            v-model:items="legendItems"
            @legend-item-click="handleLegendItemClick"
        />

        <VisXYContainer
            :data="data"
            :style="{ height: isMounted ? '100%' : 'auto' }"
            :margin="margin"
        >
            <ChartCrosshair
                v-if="showTooltip"
                :colors="colors"
                :items="legendItems"
                :custom-tooltip="customTooltip"
                :index="index"
            />

            <template v-for="(category, i) in categories" :key="category">
                <VisLine
                    :x="(d: Data, idx: number) => idx"
                    :y="(d: Data) => d[category]"
                    :color="colors[i]"
                    :curve-type="curveTypeValue"
                    :line-width="lineWidth"
                    :attributes="{
                        opacity: legendItems[i]?.inactive ? filterOpacity : 1,
                    }"
                />
            </template>

            <VisAxis
                v-if="showXAxis"
                type="x"
                :tick-format="xFormatter ?? ((v: number) => data[v]?.[index])"
                :grid-line="false"
                :tick-line="false"
                tick-text-color="hsl(var(--vis-text-color))"
            />
            <VisAxis
                v-if="showYAxis"
                type="y"
                :tick-line="false"
                :tick-format="yFormatter"
                :domain-line="false"
                :grid-line="showGridLine"
                :attributes="{
                    [Axis.selectors.grid]: {
                        class: 'text-stone-100 dark:text-stone-800',
                    },
                }"
                tick-text-color="hsl(var(--vis-text-color))"
            />

            <slot />
        </VisXYContainer>
    </div>
</template>
